import { useSearchParams } from 'react-router-dom';
import type { CoachState } from '../fixtures/coach-states';
import type { UserPersona } from '../fixtures/user';

export type RaceState = 'present' | 'none';

export type ResolvedState = {
  user: UserPersona;
  coach: CoachState;
  race: RaceState;
};

const USERS: readonly UserPersona[] = ['ramiro', 'newbie'] as const;
const COACHES: readonly CoachState[] = [
  'active',
  'pattern',
  'selfcoach',
  'newuser',
  'onboarding_incomplete',
] as const;
const RACES: readonly RaceState[] = ['present', 'none'] as const;

function isOneOf<T extends string>(
  value: string | null,
  options: readonly T[],
): value is T {
  return value !== null && (options as readonly string[]).includes(value);
}

/**
 * Reads ?user=, ?coach=, ?race= from URL search params; validates against
 * known values (falls back to defaults on unknown input); applies persona
 * locks.
 *
 * Defaults: user=ramiro, coach=active, race=present.
 *
 * Persona locks (from Decisions & Handoff §4b):
 * - user=newbie → plan is null → coach=onboarding_incomplete AND race=none,
 *   regardless of what the URL says for those params. The whole dashboard
 *   switches to the setup-first skin.
 *
 * Usage in components:
 *   const { user, coach, race } = useQueryState();
 */
export function useQueryState(): ResolvedState {
  const [params] = useSearchParams();

  const rawUser = params.get('user');
  const rawCoach = params.get('coach');
  const rawRace = params.get('race');

  const user: UserPersona = isOneOf(rawUser, USERS) ? rawUser : 'ramiro';

  // Newbie locks coach + race regardless of URL overrides.
  if (user === 'newbie') {
    return { user: 'newbie', coach: 'onboarding_incomplete', race: 'none' };
  }

  const coach: CoachState = isOneOf(rawCoach, COACHES) ? rawCoach : 'active';
  const race: RaceState = isOneOf(rawRace, RACES) ? rawRace : 'present';

  return { user, coach, race };
}
